import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { FontIcon } from 'react-md';

import {
    hideProfileModal as hideProfileModalAction,
    hideSettingsModal as hideSettingsModalAction,
    hideSignOutDialog as hideSignOutDialogAction,
} from '../actions';
import ProfileModal from './profile/ProfileModal';
import SettingsModal from './SettingsModal';
import SignOutDialog from '../../../components/Dialog/SignOutDialog';
import Auth from '../../../services/api/auth';
import AuthenticatedUserButton from '../components/AuthenticatedUserButton';
import UnauthenticatedUserButton from '../components/UnauthenticatedUserButton';
import KioskUserButton from '../components/KioskUserButton';
import Notifications from '../../Notifications/containers/Notifications';
import { getLocalAppStateAsync } from '../../../services/api/db';
import { useGlobalState } from '../../../utils/container';
import useSocietySettings from '../../Society/hooks/useSocietySettings';
import { withRouter } from 'react-router-dom';
import * as palette from '../../../components/General/Variables';
import { getVirtualEventSession } from '../../../services/api/eureka';
import entities from '../../VirtualModeration/features/virtualFeature/constants/entities';
import { useTheme } from '../../../components/Theme/ThemeContext';

const { virtualEventUserEntity } = entities;

const Container = styled.div`
    display: flex;
    float: right;
`;

const ReportProblemContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    width: 40px;
    border-radius: 50%;
    margin-right: 8px;
    background-color: #efefef;
    cursor: pointer;

    i {
        font-size: 24px !important;
        color: ${palette.BLACK_87} !important;
    }

    &:hover {
        background: rgba(0, 0, 0, 0.04);
    }
`;

const MODERATOR_DASHBOARD_LINK = 'https://knowledge.conferencecompass.com/moderator-dashboard';
const SPEAKER_DASHBOARD_LINK = 'https://knowledge.conferencecompass.com/speaker-dashboard';
const HOST_DASHBOARD_LINK = 'https://knowledge.conferencecompass.com/host-dashboard';

const AuthButton = props => {
    const isAuthenticated = Auth.isUserAuthenticated();
    const stateCtx = useGlobalState();
    const [eventId, setEventId] = useState(null);
    const [currentVirtualUserRole, setCurrentVirtualUserRole] = useState(null);
    const [releaseNum, setReleaseNum] = useState(null);
    const { socket } = stateCtx;
    const { theme } = useTheme();
    const user = Auth.getUser();

    useEffect(() => {
        (async () => {
            const appState = await getLocalAppStateAsync();
            let virtualEventSession;
            if (props.match?.params?.timeslotId) {
                virtualEventSession = await getVirtualEventSession(props.match?.params?.timeslotId);
            }
            const virtualUsers = virtualEventSession?.VirtualEventUsers || [];
            const currentVirtualUser = virtualUsers.find(
                virtualUser => virtualUser.UserId === user.id,
            );
            setEventId(appState.eventId);
            setReleaseNum(appState.releaseNum);
            setCurrentVirtualUserRole(currentVirtualUser?.role);
        })();
    }, []);

    useEffect(() => {
        if (socket && eventId && releaseNum) {
            socket.on(`new_data_release_${eventId}`, data => update(data, releaseNum));
        }
        return () => {
            if (socket && eventId && releaseNum) {
                socket.removeAllListeners(`new_data_release_${eventId}`);
            }
        };
    }, [socket, eventId, releaseNum]);

    const update = (data, releaseNum) => {
        if (data?.releaseNum > releaseNum && props.onServiceWorkerUpdate) {
            props.onServiceWorkerUpdate();
        }
    };

    const { isSociety } = useSocietySettings();
    const isVirtualSession =
        props.location.pathname &&
        (props.location.pathname.toLowerCase().indexOf('virtual-session-round') !== -1 ||
            props.location.pathname.toLowerCase().indexOf('on-demand-poster-room') !== -1 ||
            props.location.pathname.toLowerCase().indexOf('virtual-session') !== -1 ||
            props.location.pathname.toLowerCase().indexOf('virtual-moderation') !== -1);

    const openSupportPage = () => {
        if (currentVirtualUserRole === virtualEventUserEntity.role.moderator) {
            window.open(MODERATOR_DASHBOARD_LINK, '_blank');
        } else if (currentVirtualUserRole === virtualEventUserEntity.role.presenter) {
            window.open(SPEAKER_DASHBOARD_LINK, '_blank');
        } else {
            window.open(HOST_DASHBOARD_LINK, '_blank');
        }
    };

    return (
        <React.Fragment>
            {props.showProfileModal && <ProfileModal onClose={props.hideProfileModal} />}
            {props.showSettingsModal && <SettingsModal onClose={props.hideSettingsModal} />}
            {props.showSignOutDialog && (
                <SignOutDialog hideConfirmDialog={props.hideSignOutDialog} />
            )}
            {theme.kiosk ? (
                <KioskUserButton
                    light={props.light}
                    newVersionAvailable={props.newVersionAvailable}
                    updateServiceWorker={props.updateServiceWorker}
                    waitingWorker={props.waitingWorker}
                />
            ) : (
                <>
                    {isAuthenticated && (
                        <Container>
                            {/* Notificatios are disabled till they are properly fixed to support society ones */}
                            {!isSociety && <Notifications light={props.light} />}
                            {isSociety && isVirtualSession && (
                                <ReportProblemContainer onClick={openSupportPage}>
                                    <FontIcon>help</FontIcon>
                                </ReportProblemContainer>
                            )}

                            <AuthenticatedUserButton
                                isSociety={isSociety}
                                light={props.light}
                                newVersionAvailable={props.newVersionAvailable}
                                updateServiceWorker={props.updateServiceWorker}
                            />
                        </Container>
                    )}
                    {!isAuthenticated && (
                        <UnauthenticatedUserButton
                            isSociety={isSociety}
                            light={props.light}
                            newVersionAvailable={props.newVersionAvailable}
                            updateServiceWorker={props.updateServiceWorker}
                            waitingWorker={props.waitingWorker}
                        />
                    )}
                </>
            )}
        </React.Fragment>
    );
};

const mapStateToProps = state => {
    return {
        showProfileModal: state.auth.showProfileModal,
        showSettingsModal: state.auth.showSettingsModal,
        showSignOutDialog: state.auth.showSignOutDialog,
    };
};

export default connect(mapStateToProps, {
    hideProfileModal: hideProfileModalAction,
    hideSettingsModal: hideSettingsModalAction,
    hideSignOutDialog: hideSignOutDialogAction,
})(withRouter(AuthButton));
